<template>
  <section class="po-assignment min-w-0 space-y-4">
    <v-alert v-if="store.error" type="error" :text="store.error" />
    <v-progress-linear v-else-if="store.loading" indeterminate color="primary" />

    <template v-else-if="selection && context">
      <header class="shrink-0 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">PO Assignment</p>
          <h1 class="text-2xl font-semibold text-gray-900">{{ selection.vendorName }} / {{ selection.typeLabel }}</h1>
          <p class="mt-1 text-sm text-gray-500">Week starting {{ formatWeek(selection.allocation.weekKey) }}</p>
        </div>
        <router-link
          class="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-50"
          :to="calendarLink"
        >
          <v-icon icon="mdi-calendar-month-outline" size="16" />
          Back to calendar
        </router-link>
      </header>

      <dl class="shrink-0 grid grid-cols-2 gap-3 rounded-xl border border-gray-200 bg-white p-4 sm:grid-cols-4">
        <div>
          <dt class="text-xs text-gray-500">Planning capacity</dt>
          <dd class="text-lg font-semibold tabular-nums text-gray-900">{{ formatQuantity(planningCapacity) }}</dd>
        </div>
        <div>
          <dt class="text-xs text-gray-500">Allocated</dt>
          <dd class="text-lg font-semibold tabular-nums text-gray-900">{{ formatQuantity(allocatedQuantity) }}</dd>
        </div>
        <div>
          <dt class="text-xs text-gray-500">Remaining</dt>
          <dd class="text-lg font-semibold tabular-nums text-gray-900">{{ formatQuantity(selection.allocation.remainedCapacity) }}</dd>
        </div>
        <div>
          <dt class="text-xs text-gray-500">Utilization</dt>
          <dd class="text-lg font-semibold tabular-nums text-gray-900">{{ utilizationPercent }}%</dd>
        </div>
      </dl>

      <section v-if="selection.allocation.purchaseOrders.length" class="shrink-0 rounded-xl border border-gray-200 bg-white p-4" aria-label="Assigned purchase orders">
        <h2 class="text-sm font-semibold text-gray-900">Assigned to this week</h2>
        <ul class="mt-2 max-h-40 divide-y divide-gray-100 overflow-y-auto">
          <li
            v-for="po in selection.allocation.purchaseOrders"
            :key="po.id ?? po.poNumber ?? ''"
            class="flex items-center justify-between gap-3 py-2"
            :class="{ 'capacity-highlight-flash': po.poNumber && isRecentlyAssigned(po.poNumber) }"
          >
            <div>
              <strong class="text-sm text-gray-900">{{ assignmentLabel(po) }}</strong>
              <span
                v-if="po.productCategory"
                class="ml-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                :style="{ background: categoryTagFor(po.productCategory).background, color: categoryTagFor(po.productCategory).color }"
              >
                {{ categoryTagFor(po.productCategory).label }}
              </span>
              <span class="ml-2 text-xs text-gray-500">{{ po.quantity === 0 ? '0 · not counted' : `${formatQuantity(po.quantity)} · ${po.status ?? 'planned'}` }}</span>
              <p v-if="po.notes" class="m-0 text-xs text-gray-400">{{ po.notes }}</p>
            </div>
            <button
              v-if="po.id"
              type="button"
              class="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-700"
              :aria-label="`Remove ${assignmentLabel(po)} from this allocation`"
              @click="requestRemoveAssignment(po)"
            >
              <v-icon icon="mdi-trash-can-outline" size="16" />
            </button>
          </li>
        </ul>
      </section>

      <section class="flex min-h-0 flex-1 flex-col rounded-xl border border-gray-200 bg-white p-4" aria-label="Assign purchase orders">
        <div class="flex shrink-0 flex-wrap items-center justify-between gap-3">
          <div class="flex flex-wrap gap-1.5" role="group" aria-label="Filter by status">
            <button
              v-for="state in ODOO_PO_STATES"
              :key="state.value"
              type="button"
              class="rounded-full border px-3 py-1 text-xs"
              :class="picker.poStore.states.includes(state.value) ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-600'"
              @click="picker.toggleState(state.value)"
            >
              {{ state.label }}
            </button>
          </div>
          <div class="flex items-center gap-4">
            <v-text-field
              v-model="picker.poStore.search"
              class="po-assignment__search"
              label="Search PO number"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              hide-details
            />
          </div>
        </div>

        <p v-if="picker.poStore.error" class="mt-3 text-sm text-red-700" role="alert">{{ picker.poStore.error }}</p>
        <p v-else-if="picker.poStore.loading" class="mt-3 text-sm text-gray-500">Loading purchase orders…</p>
        <p v-else-if="picker.poStore.items.length === 0" class="mt-3 text-sm text-gray-500">No purchase orders match — try widening the status filter.</p>
        <p v-else-if="picker.visibleItems.value.length === 0" class="mt-3 text-sm text-gray-500">
          All {{ picker.poStore.items.length }} matching POs are for other types.
        </p>

        <div v-else class="mt-3 min-h-0 flex-1 overflow-y-auto overflow-x-auto">
          <table class="w-full border-collapse text-sm">
            <thead class="sticky top-0 z-10 bg-white">
              <tr class="border-b border-gray-200 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th class="w-8 py-2"></th>
                <th class="w-8 py-2"></th>
                <th class="py-2 pr-3">PO</th>
                <th class="py-2 pr-3">Ordered</th>
                <th class="py-2 pr-3">Expected</th>
                <th class="py-2 pr-3">Counts</th>
                <th class="py-2 pr-3">Assigned weeks</th>
                <th class="py-2 pr-3 text-right">Quantity</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="po in picker.visibleItems.value" :key="po.poNumber">
                <tr class="border-b border-gray-100" :class="{ 'bg-blue-50/60': po.poNumber in picker.selected.value }">
                  <td class="py-2 align-top">
                    <input
                      type="checkbox"
                      :checked="po.poNumber in picker.selected.value"
                      :disabled="!picker.isSelectable(po)"
                      :aria-label="`Select ${po.poNumber}`"
                      @change="picker.toggle(po)"
                    />
                  </td>
                  <td class="py-2 align-top">
                    <button type="button" class="text-gray-400 hover:text-gray-700" :aria-label="`Toggle lines for ${po.poNumber}`" @click="toggleExpanded(po.poNumber)">
                      <v-icon :icon="expanded.has(po.poNumber) ? 'mdi-chevron-down' : 'mdi-chevron-right'" size="18" />
                    </button>
                  </td>
                  <td class="py-2 pr-3 align-top">
                    <strong class="text-gray-900">{{ po.poNumber }}</strong>
                    <span
                      v-if="po.productCategory"
                      class="ml-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                      :style="{ background: categoryTagFor(po.productCategory).background, color: categoryTagFor(po.productCategory).color }"
                    >
                      {{ categoryTagFor(po.productCategory).label }}
                    </span>
                    <span v-if="picker.isResize(po.poNumber)" class="block text-xs text-gray-500">
                      already in this allocation · {{ picker.assignedQuantityFor(po.poNumber).toLocaleString() }} {{ context.typeCode }}
                    </span>
                    <template v-if="!picker.isResize(po.poNumber)">
                      <span v-if="picker.missingTypeCodesFor(po).length" class="block text-xs font-semibold text-red-700" role="alert">
                        No {{ picker.missingTypeCodesFor(po).join(', ') }} allocation{{ picker.missingTypeCodesFor(po).length > 1 ? 's' : '' }} for this week
                      </span>
                      <span v-else-if="picker.isFullyAssignedFor(po)" class="block text-xs font-semibold text-red-700" role="alert">
                        PO already fully assigned to this week
                      </span>
                    </template>
                  </td>
                  <td class="py-2 pr-3 align-top tabular-nums">{{ formatQuantity(orderedTotal(po)) }}</td>
                  <td class="py-2 pr-3 align-top">{{ formatDate(po.expectedDate) }}</td>
                  <td class="py-2 pr-3 align-top">
                    <span
                      v-for="(total, typeCode) in po.terminationTotals"
                      :key="typeCode"
                      class="mr-2 whitespace-nowrap text-xs"
                      :class="typeCode === context.typeCode ? 'font-bold text-gray-900' : 'text-gray-400'"
                    >
                      {{ typeCode }} {{ total.toLocaleString() }}
                    </span>
                    <span v-if="po.estimatedTotal > 0" class="whitespace-nowrap text-xs italic text-amber-700" title="Estimated from Odoo fiber count">
                      ~{{ po.estimatedTotal.toLocaleString() }} est.
                    </span>
                    <v-tooltip v-if="po.warnings.length" location="top">
                      <template #activator="{ props: tooltipProps }">
                        <v-icon v-bind="tooltipProps" icon="mdi-alert-outline" size="16" class="text-amber-600" />
                      </template>
                      <span v-for="warning in po.warnings" :key="warning">{{ warning }}<br /></span>
                    </v-tooltip>
                  </td>
                  <td class="py-2 pr-3 align-top text-xs text-gray-500">
                    <span v-for="assignment in po.assignments" :key="assignment.allocationId" class="block whitespace-nowrap">
                      wk {{ assignment.weekStart }} · {{ assignment.quantity.toLocaleString() }}
                    </span>
                    <span v-if="po.assignments.length === 0">—</span>
                  </td>
                  <td class="py-2 pr-3 text-right align-top">
                    <div v-if="po.poNumber in picker.selected.value" class="flex flex-col items-end gap-1">
                      <!-- A non-manufacturing PO gets no quantity field at all: it is planned into the
                           week for visibility and can never draw down capacity. -->
                      <p v-if="picker.isNonCapacityPo(po.poNumber)" class="m-0 text-right text-[11px] text-gray-400">
                        {{ po.productCategory ?? 'Non-manufacturing' }} PO — does not consume {{ context.typeCode }} capacity.
                      </p>
                      <template v-else>
                        <div v-for="(quantity, typeId) in picker.selected.value[po.poNumber]" :key="typeId" class="flex items-center gap-1.5">
                          <label class="text-xs text-gray-500" :for="`assign-qty-${po.poNumber}-${typeId}`">{{ picker.typeCodeForId(typeId) }}</label>
                          <input
                            :id="`assign-qty-${po.poNumber}-${typeId}`"
                            v-model.number="picker.selected.value[po.poNumber][typeId]"
                            type="number"
                            :min="0"
                            class="w-28 rounded-md border border-gray-300 px-2 py-1 text-right text-sm tabular-nums"
                            :aria-label="`Quantity (${picker.typeCodeForId(typeId)}) for ${po.poNumber}`"
                          />
                        </div>
                        <p v-if="picker.isResize(po.poNumber)" class="m-0 text-right text-[11px] text-gray-400">
                          Replaces the {{ picker.assignedQuantityFor(po.poNumber).toLocaleString() }} already assigned here.
                        </p>
                        <p
                          v-else-if="picker.allowsZeroQuantity(po.poNumber) && (picker.selected.value[po.poNumber][context.capacityTypeId] ?? 0) === 0"
                          class="m-0 text-right text-[11px] text-gray-400"
                        >
                          Not counted toward capacity — enter a quantity to count it.
                        </p>
                      </template>
                    </div>
                  </td>
                </tr>
                <tr v-if="expanded.has(po.poNumber)" class="border-b border-gray-100 bg-gray-50/60">
                  <td></td>
                  <td></td>
                  <td colspan="6" class="py-2 pr-3">
                    <table class="w-full text-xs">
                      <thead>
                        <tr class="text-left text-gray-400">
                          <th class="py-1 pr-3 font-medium">Part number</th>
                          <th class="py-1 pr-3 font-medium">Description</th>
                          <th class="py-1 pr-3 font-medium">Ordered</th>
                          <th class="py-1 pr-3 font-medium">Received</th>
                          <th class="py-1 pr-3 font-medium">Per-unit counts</th>
                          <th class="py-1 pr-3 font-medium">Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(line, index) in po.lines" :key="index" class="text-gray-600">
                          <td class="py-1 pr-3 font-mono">{{ line.partNumber ?? '—' }}</td>
                          <td class="max-w-md truncate py-1 pr-3" :title="line.description">{{ line.description }}</td>
                          <td class="py-1 pr-3 tabular-nums">{{ line.quantity.toLocaleString() }}</td>
                          <td class="py-1 pr-3 tabular-nums">{{ line.qtyReceived.toLocaleString() }}</td>
                          <td class="py-1 pr-3">{{ formatCounts(line) }}</td>
                          <td class="py-1 pr-3">{{ formatCountSource(line.countSource) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <footer class="mt-4 flex shrink-0 flex-wrap items-center justify-between gap-3">
          <p v-if="picker.hiddenCount.value > 0 && picker.visibleItems.value.length > 0" class="m-0 w-full text-xs text-gray-400">
            {{ picker.hiddenCount.value }} PO{{ picker.hiddenCount.value === 1 ? '' : 's' }} hidden — no {{ context.typeCode }} allocations.
          </p>
          <p class="m-0 text-sm" :class="picker.overCapacity.value ? 'font-semibold text-amber-700' : 'text-gray-500'">
            <template v-if="picker.selectedTotal.value < 0">Returns {{ Math.abs(picker.selectedTotal.value).toLocaleString() }} to this week</template>
            <template v-else>
              Selected {{ picker.selectedTotal.value.toLocaleString() }} of {{ selection.allocation.remainedCapacity.toLocaleString() }} remaining
            </template>
            <template v-if="picker.overCapacity.value"> — over capacity (allowed, the week will show as overbooked)</template>
          </p>
          <button
            type="button"
            class="min-h-10 rounded-md bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
            :disabled="!picker.canSubmit.value || picker.submitting.value"
            @click="picker.requestSubmit"
          >
            {{ picker.submitting.value ? 'Assigning…' : submitLabel }}
          </button>
        </footer>
      </section>
    </template>

    <ConfirmDialog
      :open="picker.pendingOverbook.value !== null"
      title="Overbook this week?"
      :message="overbookMessage"
      confirm-label="Assign anyway"
      @confirm="picker.submitConfirmed"
      @cancel="picker.pendingOverbook.value = null"
    />

    <ConfirmDialog
      :open="pendingRemoval !== null"
      title="Remove purchase order?"
      :message="removalConfirmMessage"
      confirm-label="Remove"
      @confirm="confirmRemoveAssignment"
      @cancel="pendingRemoval = null"
    />
  </section>
</template>

<script setup lang="ts">
  import { computed, onMounted, reactive, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';

  import ConfirmDialog from '@/components/common/ConfirmDialog.vue';
  import { usePoAssignment, type PoAssignmentContext } from '@/composables/usePoAssignment';
  import { useRecentAssignmentHighlight } from '@/composables/useRecentAssignmentHighlight';
  import { useCapacityCalendarStore } from '@/store/capacityCalendarStore';
  import { ODOO_PO_STATES, usePurchaseOrderStore } from '@/store/purchaseOrderStore';
  import { ASSIGNMENT_TYPE } from '@/types/capacityCalendar';
  import type { PoAllocation, SelectedAllocation } from '@/types/capacityCalendar';
  import type { PurchaseOrder, PurchaseOrderLine } from '@/types/purchaseOrders';
  import { assignmentLabel, resolveDistributionTypeCodes } from '@/utils/capacityCalendar';
  import { categoryTagFor } from '@/utils/categoryTags';

  const FULL_UTILIZATION_PERCENT = 100;

  const store = useCapacityCalendarStore();
  const route = useRoute();
  const router = useRouter();
  const expanded = reactive(new Set<string>());

  const allocationKey = computed(() => {
    const queryValue = route.query.allocation;
    return Array.isArray(queryValue) ? (queryValue[0] ?? null) : (queryValue ?? null);
  });

  const selection = computed<SelectedAllocation | null>(() => {
    if (!allocationKey.value) return null;
    const [rowId, weekKey] = allocationKey.value.split('|');
    const row = store.rows.find((candidate) => candidate.id === rowId);
    const allocation = store.allocations[rowId]?.[weekKey];
    return row && allocation
      ? {
          allocation,
          rowId,
          vendorName: row.vendorName,
          typeLabel: row.typeLabel,
          vendorId: row.vendorId,
          capacityTypeId: row.capacityTypeId,
          typeCode: row.typeCode,
          category: row.category,
        }
      : null;
  });

  const context = computed<PoAssignmentContext | null>(() => {
    const current = selection.value;
    return current?.vendorId && current.capacityTypeId && current.typeCode
      ? {
          vendorId: current.vendorId,
          capacityTypeId: current.capacityTypeId,
          typeCode: current.typeCode,
          rowId: current.rowId,
          weekKey: current.allocation.weekKey,
          remainingCapacity: current.allocation.remainedCapacity,
          // A manual block's poNumber is a tracking reference, not a PO commitment.
          assignedPoNumbers: current.allocation.purchaseOrders.flatMap((po) =>
            po.type !== ASSIGNMENT_TYPE.manual && po.poNumber ? [po.poNumber] : [],
          ),
          category: current.category,
        }
      : null;
  });

  const poStore = usePurchaseOrderStore();

  // The composable needs a context on first call; fall back to empty values
  // until the store resolves — the guard in onMounted redirects invalid links.
  const picker = usePoAssignment(
    () =>
      context.value ?? {
        vendorId: '',
        capacityTypeId: '',
        typeCode: '',
        rowId: '',
        weekKey: '',
        remainingCapacity: 0,
        assignedPoNumbers: [],
      },
    () => {
      const vendorId = context.value?.vendorId;
      if (vendorId) void poStore.load(vendorId);
    },
  );

  const { matchingPoNumbers } = useRecentAssignmentHighlight();
  const recentlyAssignedPoNumbers = computed(() => matchingPoNumbers(selection.value?.allocation.weekKey));
  function isRecentlyAssigned(poNumber: string): boolean {
    return recentlyAssignedPoNumbers.value.has(poNumber);
  }

  const allocatedQuantity = computed(() => selection.value?.allocation.purchaseOrders.reduce((total, po) => total + po.quantity, 0) ?? 0);
  const planningCapacity = computed(() => selection.value?.allocation.planningCapacity ?? allocatedQuantity.value + Math.max(selection.value?.allocation.remainedCapacity ?? 0, 0));
  const utilizationPercent = computed(() => (planningCapacity.value > 0 ? Math.round((allocatedQuantity.value / planningCapacity.value) * FULL_UTILIZATION_PERCENT) : 0));
  const calendarLink = computed(() => ({ path: '/capacity-calendar', query: { allocation: allocationKey.value ?? undefined } }));

  // A selection made purely of POs already on this allocation updates rows rather than
  // adding them; a mixed selection keeps the plain "assign" wording.
  const submitLabel = computed(() => {
    const count = picker.selectedCount.value;
    const poNumbers = Object.keys(picker.selected.value);
    const verb = poNumbers.length > 0 && poNumbers.every((poNumber) => picker.isResize(poNumber)) ? 'Update' : 'Assign';
    return `${verb} ${count} PO${count === 1 ? '' : 's'}`;
  });

  const overbookMessage = computed(() => {
    const overbooked = picker.pendingOverbook.value;
    const weekKey = selection.value?.allocation.weekKey;
    if (!overbooked || !weekKey) return '';
    const list = overbooked.map((entry) => `${entry.typeCode} by ${entry.by.toLocaleString()}`).join(', ');
    return `This will overbook ${list} for the week of ${formatWeek(weekKey)} — this may cause problems. Assign anyway?`;
  });

  function toggleExpanded(poNumber: string): void {
    if (expanded.has(poNumber)) {
      expanded.delete(poNumber);
      return;
    }
    expanded.add(poNumber);
  }

  function orderedTotal(po: PurchaseOrder): number {
    return po.lines.reduce((total, line) => total + line.quantity, 0);
  }

  function formatCounts(line: PurchaseOrderLine): string {
    if (line.countsPerUnit && Object.keys(line.countsPerUnit).length > 0) {
      return Object.entries(line.countsPerUnit)
        .map(([typeCode, perUnit]) => `${typeCode} ×${perUnit}`)
        .join(', ');
    }
    return line.countSource === 'fiberCount' ? `~${line.fiberCount} fibers` : '—';
  }

  function formatCountSource(source: PurchaseOrderLine['countSource']): string {
    if (source === 'parser') return 'parsed';
    if (source === 'fiberCount') return 'est. fiber count';
    return '—';
  }

  function formatDate(value: string | null): string {
    return value ? value.slice(0, 'YYYY-MM-DD'.length) : '—';
  }

  function formatQuantity(value: number): string {
    return value.toLocaleString();
  }

  function formatWeek(weekKey: string): string {
    const [year, month, day] = weekKey.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  const pendingRemoval = ref<PoAllocation | null>(null);
  const removalConfirmMessage = computed(() => {
    const purchaseOrder = pendingRemoval.value;
    const poNumber = purchaseOrder?.poNumber ?? '';
    const genericMessage = `${poNumber} was distributed across this week's capacity types — removing it removes all portions.`;
    const vendorId = selection.value?.vendorId;
    const weekKey = selection.value?.allocation.weekKey;
    const distributionGroupId = purchaseOrder?.distributionGroupId;
    if (!vendorId || !weekKey || !distributionGroupId) {
      return genericMessage;
    }

    const codes = resolveDistributionTypeCodes({ rows: store.rows, allocations: store.allocations, vendorId, weekKey, distributionGroupId });

    if (codes.length < 2) {
      return genericMessage;
    }
    return `${poNumber} was distributed across ${codes.join(', ')} for this week — removing it removes all portions.`;
  });

  async function removeAssignment(assignmentId: string): Promise<void> {
    if (!selection.value) return;
    await store.removePoAssignment(selection.value.rowId, selection.value.allocation.weekKey, assignmentId);
  }

  function requestRemoveAssignment(purchaseOrder: PoAllocation): void {
    if (!purchaseOrder.id) {
      return;
    }
    if (purchaseOrder.distributionGroupId) {
      pendingRemoval.value = purchaseOrder;
      return;
    }
    void removeAssignment(purchaseOrder.id);
  }

  async function confirmRemoveAssignment(): Promise<void> {
    const purchaseOrder = pendingRemoval.value;
    pendingRemoval.value = null;
    if (purchaseOrder?.id) {
      await removeAssignment(purchaseOrder.id);
    }
  }

  onMounted(async () => {
    if (store.rows.length === 0) {
      await store.load();
    }
    if (!selection.value || !context.value) {
      await router.replace({ path: '/capacity-calendar' });
    }
  });
</script>

<style scoped>
  .po-assignment__search {
    min-width: 16rem;
  }

  /* Pin the page to the viewport (header ≈ 3.5rem + main padding 3rem) so the
     PO table scrolls internally and the Assign footer never leaves the screen. */
  .po-assignment {
    display: flex;
    height: calc(100vh - 6.5rem);
    flex-direction: column;
  }
</style>
